import classNames from 'classnames';
import { useState } from 'react';

import { DemoPill } from '@/components/ui/controls';
import type { LeadDetail } from '@/types/api';
import { formatDateTime } from '@/utils/format';
import http from '@/utils/http';

import { Empty } from './lead-parts';

// 44px tall with 16px text on a phone, so iOS does not zoom in (UI-1).
const inputCls =
  'h-11 min-w-0 rounded-lg bg-control px-3 text-base text-fg shadow-ctl placeholder:text-fg-faint focus:outline-none focus:ring-2 focus:ring-brand/50 md:h-9 md:text-sm';

/** The demo seed's follow-ups start with "Demo: " (backend/deploy/local/prisma_demo.sql). */
export const FollowUpsDemo = ({ lead }: { lead: LeadDetail }) =>
  lead.follow_ups.some((f) => f.note.startsWith('Demo: ')) ? <DemoPill title="From the demo seed">Demo</DemoPill> : null;

/**
 * Follow-ups on the lead (REM-1). When one is due, the bot reminds the team chat once.
 * The side panel counts the done ones; the full lead page lists them (`showDone`).
 */
export const FollowUps = ({ lead, onChange, showDone = false }: { lead: LeadDetail; onChange: () => void; showDone?: boolean }) => {
  const [due, setDue] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const open = lead.follow_ups.filter((f) => !f.done_at);
  const done = lead.follow_ups.filter((f) => f.done_at);

  const run = async (call: () => Promise<unknown>, after?: () => void) => {
    setBusy(true);
    setError(null);
    try {
      await call();
      after?.();
      onChange();
    } catch {
      setError('That did not work. Try again.');
    } finally {
      setBusy(false);
    }
  };
  const add = () =>
    due &&
    run(
      () => http.post(`/leads/${encodeURIComponent(lead.id)}/follow-ups`, { due_at: new Date(due).toISOString(), note: note.trim() }),
      () => {
        setDue('');
        setNote('');
      },
    );

  return (
    <>
      {open.length ? (
        <ul className="space-y-2">
          {open.map((f) => (
            <li key={f.id} className="flex items-start justify-between gap-3 rounded-lg bg-surface-inset py-2 pl-3 pr-1 text-sm md:pr-3">
              <div className="min-w-0">
                <p className="text-fg-strong">
                  {formatDateTime(f.due_at)}
                  {new Date(f.due_at).getTime() <= Date.now() && (
                    <span className="ml-2 rounded bg-danger/15 px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide text-danger">Due</span>
                  )}
                </p>
                {f.note && <p className="break-words text-fg">{f.note}</p>}
                <p className="text-xs text-fg-faint">
                  {[f.created_by && `Set by ${f.created_by}`, f.reminded_at && 'Reminder sent to the team chat'].filter(Boolean).join(' · ')}
                </p>
              </div>
              <button
                type="button"
                disabled={busy}
                onClick={() => run(() => http.post(`/follow-ups/${encodeURIComponent(f.id)}/done`))}
                className="h-11 shrink-0 rounded-lg px-3 text-sm text-fg-muted transition-colors hover:bg-surface-hover hover:text-fg disabled:opacity-50 md:h-8"
              >
                Done
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <Empty>No open follow-ups.</Empty>
      )}
      {done.length > 0 &&
        (showDone ? (
          <ul className="mt-3 space-y-1">
            {done.map((f) => (
              <li key={f.id} className="flex flex-wrap items-baseline justify-between gap-x-3 text-sm text-fg-muted md:flex-nowrap">
                <span className="min-w-0 break-words">{f.note || 'No note'}</span>
                <span className="text-xs text-fg-faint md:shrink-0">Done {formatDateTime(f.done_at as string)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-2 text-xs text-fg-faint">{done.length} done</p>
        ))}
      <div className="mt-3 flex flex-col gap-2">
        <input type="datetime-local" aria-label="When" className={inputCls} value={due} onChange={(e) => setDue(e.target.value)} />
        <div className="flex gap-2">
          <input
            aria-label="Note"
            placeholder="Note (optional)"
            maxLength={1000}
            className={classNames(inputCls, 'flex-1')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !busy && add()}
          />
          <button
            type="button"
            disabled={busy || !due}
            onClick={add}
            className="h-11 shrink-0 rounded-lg bg-brand px-4 text-sm text-black shadow-ctl transition hover:shadow-ctl-hover active:translate-y-px active:shadow-ctl-press hover:bg-cta-hover-bg hover:text-cta-hover-fg disabled:opacity-50 disabled:hover:bg-brand disabled:hover:text-black disabled:hover:shadow-ctl disabled:active:translate-y-0 disabled:active:shadow-ctl md:h-9"
          >
            Add
          </button>
        </div>
      </div>
      {error && <p className="mt-2 text-xs text-danger">{error}</p>}
    </>
  );
};
